/**
 * ブースターエフェクトの描画
 *
 * エンジン炎やブースターの視覚エフェクトを描画します。
 * createBoosterEffect() がパーティクル生成を担当し、
 * このモジュールは即時描画のエフェクトを担当します。
 *
 * @module features/particles/booster-effects
 */

import { getP5 } from '../../core/p5-instance';

/**
 * ブースター炎エフェクトを描画
 * 出撃シーンなどで使用する即時描画のブースター炎
 *
 * @param intensity - エフェクトの強度 (0.0 - 1.0)
 * @param color - 機体の色 [r, g, b]
 */
export function drawBoosterFlame(intensity: number, color: readonly number[]): void {
    const p = getP5();
    if (intensity <= 0) {
        return;
    }

    const clampedIntensity = p.min(1, intensity);

    // メインエンジン炎（機体色）
    const flameCount = Math.floor(8 * clampedIntensity);
    for (let i = 0; i < flameCount; i++) {
        const flameY = 30 + p.random(30, 80) * clampedIntensity;
        const flameX = p.random(-15, 15);
        const flameSize = p.random(10, 30) * clampedIntensity;
        p.fill(color[0], color[1], color[2], p.random(150, 255));
        p.noStroke();
        p.ellipse(flameX, flameY, flameSize, flameSize * 2);
    }

    // 白い芯（高温部分）
    const coreCount = Math.floor(3 * clampedIntensity);
    for (let i = 0; i < coreCount; i++) {
        p.fill(255, 255, 255, p.random(200, 255));
        p.ellipse(
            p.random(-5, 5),
            35 + p.random(10, 30),
            p.random(5, 10),
            p.random(15, 25)
        );
    }
}

/**
 * スピードラインエフェクトを描画
 * 出撃時の高速移動感を演出
 *
 * @param progress - 進行度 (0.0 - 1.0)
 */
export function drawSpeedLines(progress: number): void {
    const p = getP5();
    if (progress <= 0) {
        return;
    }

    p.stroke(255, 255, 255, 80 + progress * 100);
    p.strokeWeight(1);

    const lineCount = 15;
    for (let i = 0; i < lineCount; i++) {
        const lineX = p.random(p.width);
        const lineLen = p.random(50, 150) * progress;
        const startY = p.random(p.height);
        p.line(lineX, startY, lineX, startY + lineLen);
    }
}

/**
 * 機体別エンジン炎を描画
 * ステージ中と同じスタイルの4層グラデーション炎
 *
 * @param shipType - 機体タイプ ('bomber' | 'hunter' | 'lancer')
 * @param intensity - エフェクトの強度 (0.0 - 1.0)
 * @param size - 機体サイズ
 */
export function drawShipEngineFlames(shipType: string, intensity: number, size: number): void {
    const p = getP5();
    if (intensity <= 0) {
        return;
    }

    const enginePulse = p.sin(p.frameCount * 0.3) * 4 + 8;
    const engineY = size / 2 + 3;
    const intensityMult = p.min(1, intensity);

    switch (shipType) {
        case 'bomber':
            // BOMBER: シアン系、中央1基
            {
                const pulse = enginePulse * intensityMult;
                // 外側グロー
                p.fill(0, 150, 255, 60 * intensityMult);
                p.noStroke();
                p.ellipse(0, engineY, 18 * intensityMult, pulse + 14);
                // 中間炎
                p.fill(0, 200, 255, 100 * intensityMult);
                p.ellipse(0, engineY, 14 * intensityMult, pulse + 10);
                // 内側炎
                p.fill(100, 255, 255, 180 * intensityMult);
                p.ellipse(0, engineY, 10 * intensityMult, pulse + 6);
                // 白熱コア
                p.fill(255, 255, 255, 255 * intensityMult);
                p.ellipse(0, engineY, 6 * intensityMult, pulse);
            }
            break;

        case 'hunter':
            // HUNTER: オレンジ系、左右2基
            {
                const pulse = enginePulse * intensityMult;
                // 左エンジン
                p.fill(255, 100, 0, 60 * intensityMult);
                p.noStroke();
                p.ellipse(-10, engineY, 14 * intensityMult, pulse + 12);
                p.fill(255, 150, 0, 100 * intensityMult);
                p.ellipse(-10, engineY, 11 * intensityMult, pulse + 8);
                p.fill(255, 200, 50, 180 * intensityMult);
                p.ellipse(-10, engineY, 8 * intensityMult, pulse + 4);
                p.fill(255, 255, 200, 255 * intensityMult);
                p.ellipse(-10, engineY, 5 * intensityMult, pulse);
                // 右エンジン
                p.fill(255, 100, 0, 60 * intensityMult);
                p.ellipse(10, engineY, 14 * intensityMult, pulse + 12);
                p.fill(255, 150, 0, 100 * intensityMult);
                p.ellipse(10, engineY, 11 * intensityMult, pulse + 8);
                p.fill(255, 200, 50, 180 * intensityMult);
                p.ellipse(10, engineY, 8 * intensityMult, pulse + 4);
                p.fill(255, 255, 200, 255 * intensityMult);
                p.ellipse(10, engineY, 5 * intensityMult, pulse);
            }
            break;

        case 'lancer':
            // LANCER: 紫系、左右2基
            {
                const pulse = enginePulse * intensityMult;
                const offsetX = size / 4;
                // 左エンジン
                p.fill(200, 50, 255, 60 * intensityMult);
                p.noStroke();
                p.ellipse(-offsetX, engineY, 14 * intensityMult, pulse + 12);
                p.fill(220, 100, 255, 100 * intensityMult);
                p.ellipse(-offsetX, engineY, 11 * intensityMult, pulse + 8);
                p.fill(240, 150, 255, 180 * intensityMult);
                p.ellipse(-offsetX, engineY, 8 * intensityMult, pulse + 4);
                p.fill(255, 220, 255, 255 * intensityMult);
                p.ellipse(-offsetX, engineY, 5 * intensityMult, pulse);
                // 右エンジン
                p.fill(200, 50, 255, 60 * intensityMult);
                p.ellipse(offsetX, engineY, 14 * intensityMult, pulse + 12);
                p.fill(220, 100, 255, 100 * intensityMult);
                p.ellipse(offsetX, engineY, 11 * intensityMult, pulse + 8);
                p.fill(240, 150, 255, 180 * intensityMult);
                p.ellipse(offsetX, engineY, 8 * intensityMult, pulse + 4);
                p.fill(255, 220, 255, 255 * intensityMult);
                p.ellipse(offsetX, engineY, 5 * intensityMult, pulse);
            }
            break;
    }
}

/**
 * チャージリングエフェクトを描画
 * 出撃前のエネルギーチャージ演出
 *
 * @param centerX - 中心X座標
 * @param centerY - 中心Y座標
 * @param progress - 進行度 (0.0 - 1.0)
 * @param color - 機体の色 [r, g, b]
 * @param timer - アニメーションタイマー
 */
export function drawChargeRings(
    centerX: number,
    centerY: number,
    progress: number,
    color: readonly number[],
    timer: number
): void {
    const p = getP5();
    if (progress <= 0) {
        return;
    }

    const ringCount = p.floor(progress * 3) + 1;
    for (let r = 0; r < ringCount; r++) {
        const ringSize = 60 + r * 25 + p.sin(timer * 0.3 + r) * 5;
        const ringAlpha = 150 - r * 40;
        p.noFill();
        p.stroke(color[0], color[1], color[2], ringAlpha * progress);
        p.strokeWeight(2);
        p.circle(centerX, centerY, ringSize);
    }
}